// Public marketing page. Neutral and student-facing: it shows the learning
// product (lessons, timed practice, mocks, feedback) on simulated screens. No
// B2B / sales framing, no copy that names who buys it. Plain copy, no invented
// results, no poster lines. Sign-in top right; no self-serve signup.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Monitor, AlertTriangle, PenLine, Timer, BookOpen, LineChart,
  Mic, Headphones,
} from 'lucide-react';

import { BRAND } from '@/lib/brand';
import { cn } from '@/lib/utils';

function Wordmark({ className }) {
  return <span className={cn('h-display', className)}>{BRAND}<span className="text-indigo-600">.</span></span>;
}

function Reveal({ children, className, as: Tag = 'div' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={cn('reveal', shown && 'is-in', className)}>{children}</Tag>;
}

// Fine film grain for the dark sections (subtle texture, not a flat fill).
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
function Grain() {
  return <div aria-hidden className="pointer-events-none absolute inset-0" style={{ backgroundImage: GRAIN, opacity: 0.06, mixBlendMode: 'overlay' }} />;
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduce;
}

// The simulated screen: dark bezel, soft shadow, faint sheen, on a fine dotted
// field. Reads as the app on a device, not a flat paste. Holds one or more
// stacked frames (the animated variant crossfades between them).
function DeviceFrame({ label, className, children }) {
  return (
    <figure className={cn('relative', className)}>
      <div
        aria-hidden
        className="absolute -inset-5 -z-10 rounded-[2rem]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgb(100 116 139 / 0.18) 1px, transparent 1.2px)',
          backgroundSize: '13px 13px',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 50%, transparent 84%)',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 84%)',
        }}
      />
      <div className="relative rounded-[1.15rem] bg-gradient-to-b from-slate-700 to-slate-900 p-2 shadow-[0_34px_70px_-24px_rgb(15_23_42_/_0.5)] ring-1 ring-white/10">
        <div className="mx-auto mb-1 h-[3px] w-[3px] rounded-full bg-white/25" />
        <div className="relative overflow-hidden rounded-lg bg-white ring-1 ring-black/10">
          {children}
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-white/[0.06]" />
        </div>
      </div>
      {label && (
        <figcaption className="mt-3.5 text-center text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">{label}</figcaption>
      )}
    </figure>
  );
}

function Device({ src, label, eager, className }) {
  return (
    <DeviceFrame label={label} className={className}>
      <img src={`/shots/${src}`} alt={label || ''} loading={eager ? 'eager' : 'lazy'} className="block w-full" />
    </DeviceFrame>
  );
}

// Same frame, but the screen plays a real captured sequence: each frame is one
// step of the walkthrough, so the marks build up, the caption changes, and the
// answer reveals, just like a student stepping through it. Pauses off-screen and
// holds still under reduced-motion.
function AnimatedDevice({ frames, label, className }) {
  const [idx, setIdx] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reduce || !inView) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % frames.length), 1900);
    return () => clearInterval(t);
  }, [reduce, inView, frames.length]);

  const shown = reduce ? frames.length - 1 : idx;
  return (
    <div ref={ref}>
      <DeviceFrame label={label} className={className}>
        {frames.map((src, i) => (
          <img
            key={src}
            src={`/shots/${src}`}
            alt={i === 0 ? (label || '') : ''}
            loading="lazy"
            className={cn(
              'block w-full transition-opacity duration-[600ms] ease-in-out',
              i === 0 ? 'relative' : 'absolute inset-0',
              i === shown ? 'opacity-100' : 'opacity-0'
            )}
          />
        ))}
      </DeviceFrame>
    </div>
  );
}

const STATS = [
  { n: '2', label: 'exams, CELPIP General and PTE Core' },
  { n: '39', label: 'task types, each taught and timed' },
  { n: '1,600+', label: 'practice items, written in house' },
  { n: '60', label: 'full timed mock sittings' },
  { n: '40', label: 'strategy lessons and walkthroughs' },
  { n: '4', label: 'skills covered end to end' },
];

// Neutral, student-facing. Every image is a screen from the running app.
const GALLERY = [
  {
    src: 'practice-hub.png', label: 'Timed practice', tag: 'the whole exam',
    k: 'Every task type, in one place',
    v: 'Listening, reading, writing, and speaking are broken into each task the exam scores. You train them one at a time and watch your accuracy climb on each.',
  },
  {
    frames: ['wt-1.png', 'wt-2.png', 'wt-3.png', 'wt-4.png', 'wt-5.png', 'wt-6.png', 'wt-7.png'],
    label: 'Guided walkthrough', tag: 'learn it first',
    k: 'A worked walkthrough for every task',
    v: 'A tutor marks up a real item one step at a time, highlighting the words that decide the answer and ruling out each trap, so you see exactly how to read the task.',
  },
  {
    src: 'practice-question.png', label: 'Practice runner', tag: 'then practise',
    k: 'Practise on the test screen',
    v: 'Each question type runs on its own timed screen, with the passage or audio, the clock, and the answer area arranged the way the exam presents them.',
  },
  {
    src: 'practice-review.png', label: 'Answer review', tag: 'every answer',
    k: 'See why every answer scored',
    v: 'After each set, the right answer sits beside the wrong ones, with the specific trap each wrong choice was written to catch, so the same mistake does not return.',
  },
];

const FEATURES = [
  { icon: Monitor, title: 'The exam interface', body: 'Every practice set runs in a test-style screen: section timers, play-once audio, dropdown blanks, drag pools, forward-only navigation.' },
  { icon: AlertTriangle, title: 'Every trap, named', body: 'Each wrong option is a designed trap. After every set the screen shows which trap caught you and why, so the same miss does not repeat.' },
  { icon: BookOpen, title: 'Lessons and walkthroughs', body: 'Each task type opens with a lesson on what raters reward, then a walkthrough that solves a real item one move at a time.' },
  { icon: Timer, title: 'Full timed mocks', body: 'Sit all four sections back to back under exam rules, with no review between them, and a CLB practice estimate at the end.' },
  { icon: PenLine, title: 'Writing and speaking, by rubric', body: 'Writing and speaking are built around the official scoring rubric for each task, so you practise to what earns points, not a word count.' },
  { icon: LineChart, title: 'Readiness, tracked', body: 'Weakest tasks, repeated traps, and section accuracy roll up to one clear next move to work on.' },
];

const EXAMS = [
  {
    tag: 'CELPIP General',
    org: 'Administered by Paragon Testing Enterprises, a Canadian company.',
    accepted: 'Accepted by IRCC for permanent residence and Canadian citizenship.',
    skills: [
      { icon: Headphones, k: 'Listening', v: 'Six parts: problem solving, daily life, information, news, discussion, viewpoints' },
      { icon: BookOpen, k: 'Reading', v: 'Four parts: correspondence, a diagram, information, viewpoints' },
      { icon: PenLine, k: 'Writing', v: 'Two tasks: an email and a survey response, on the clock' },
      { icon: Mic, k: 'Speaking', v: 'Eight tasks: advice, experience, describing a scene, predictions, and more' },
    ],
  },
  {
    tag: 'PTE Core',
    org: 'Administered by Pearson, delivered on computer.',
    accepted: 'Accepted by IRCC for economic-class permanent residence.',
    skills: [
      { icon: Mic, k: 'Speaking', v: 'Read aloud, repeat sentence, describe image, respond to a situation, answer short question' },
      { icon: PenLine, k: 'Writing', v: 'Summarize written text, write email' },
      { icon: BookOpen, k: 'Reading', v: 'Fill in the blanks, re-order paragraphs, multiple choice' },
      { icon: Headphones, k: 'Listening', v: 'Summarize spoken text, dictation, highlight incorrect words, select missing word' },
    ],
  },
];

const METHOD = [
  { k: 'Learn the trap', v: 'A lesson names what the task tests and the traps built to cost points.' },
  { k: 'Watch it worked', v: 'A walkthrough solves a real item one move at a time, catching each trap.' },
  { k: 'Practise the screen', v: 'Timed sets in a test-style interface, with the trap behind every miss explained.' },
  { k: 'Sit a full mock', v: 'All four sections under exam rules, finishing with a CLB practice estimate.' },
  { k: 'Track readiness', v: 'See where you stand and the next move that matters most.' },
];

function Nav() {
  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <Wordmark className="text-xl" />
        <nav className="hidden items-center gap-8 text-sm text-slate-500 sm:flex">
          <a href="#platform" className="transition-colors hover:text-slate-900">The platform</a>
          <a href="#exams" className="transition-colors hover:text-slate-900">Exams</a>
          <a href="#method" className="transition-colors hover:text-slate-900">How it works</a>
        </nav>
        <Link to="/login" className="cta" data-testid="landing-signin">Sign in <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Nav />

      {/* hero: headline + one large screen of the product on a device */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:py-24">
          <Reveal className="max-w-2xl">
            <p className="mono-stat">CELPIP General · PTE Core</p>
            <h1 className="h-display mt-4 text-5xl leading-[1.05] sm:text-6xl">Prepare for CELPIP General and PTE Core.</h1>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-slate-600">
              Both English exams Canada accepts for immigration, taught task by task and practised under
              each exam's format and timing, with full timed mocks and a readiness estimate.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/login" className="cta" data-testid="landing-hero-signin">Sign in to your portal <ArrowRight className="h-4 w-4" /></Link>
              <a href="#platform" className="cta-quiet">See the platform</a>
            </div>
          </Reveal>
          <Reveal className="mt-16">
            <Device src="dashboard.png" label="Dashboard" eager className="mx-auto max-w-4xl" />
          </Reveal>
        </div>
      </section>

      {/* stat band */}
      <section className="relative overflow-hidden bg-slate-900">
        <Grain />
        <div className="relative mx-auto grid max-w-6xl grid-cols-2 gap-y-8 px-6 py-12 md:grid-cols-3 lg:grid-cols-6">
          {STATS.map((s) => (
            <Reveal key={s.label} className="px-2">
              <p className="h-display text-3xl text-white tabular-nums">{s.n}</p>
              <p className="mt-1 text-[12.5px] leading-snug text-slate-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* the product, on screen */}
      <section id="platform" className="mx-auto max-w-6xl px-6 py-20">
        <Reveal>
          <p className="mono-stat">inside the platform</p>
          <h2 className="h-display mt-3 max-w-2xl text-3xl leading-tight sm:text-4xl">Built around the exam, task by task.</h2>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-600">
            Lessons, timed practice on every task type, full mock tests, and feedback that explains each
            answer. Here is what that looks like on screen.
          </p>
        </Reveal>
        <div className="mt-16 space-y-20 lg:space-y-28">
          {GALLERY.map((g, i) => (
            <div key={g.src} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
              <Reveal className={cn(i % 2 === 1 && 'lg:order-2')}>
                {g.frames
                  ? <AnimatedDevice frames={g.frames} label={g.label} />
                  : <Device src={g.src} label={g.label} />}
              </Reveal>
              <Reveal className={cn(i % 2 === 1 && 'lg:order-1')}>
                <p className="mono-stat">{g.tag}</p>
                <h3 className="h-display mt-3 text-2xl leading-tight sm:text-[27px]">{g.k}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{g.v}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* what's inside */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <Reveal>
            <p className="mono-stat">what's inside</p>
            <h2 className="h-display mt-3 max-w-2xl text-3xl leading-tight sm:text-4xl">What the platform does.</h2>
          </Reveal>
          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <Reveal key={f.title}>
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display mt-4 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{f.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* the two exams */}
      <section id="exams" className="mx-auto max-w-6xl px-6 py-20">
        <Reveal>
          <p className="mono-stat">the two exams</p>
          <h2 className="h-display mt-3 max-w-2xl text-3xl leading-tight sm:text-4xl">CELPIP General and PTE Core.</h2>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-600">
            Both are English-language tests IRCC accepts for Canadian immigration. This platform is an
            independent preparation tool and is not affiliated with or endorsed by either testing
            organization.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {EXAMS.map((e) => (
            <Reveal key={e.tag} className="rounded-xl border border-slate-200 bg-white p-7">
              <p className="font-display text-xl font-semibold text-slate-900">{e.tag}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{e.org}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-400">{e.accepted}</p>
              <ul className="mt-6 space-y-4">
                {e.skills.map((s) => (
                  <li key={s.k} className="flex gap-3.5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">
                      <s.icon className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-[14.5px] font-semibold text-slate-900">{s.k}</span>
                      <span className="block text-[13.5px] leading-snug text-slate-500">{s.v}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      {/* how it works */}
      <section id="method" className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <Reveal>
            <p className="mono-stat">how it works</p>
            <h2 className="h-display mt-3 max-w-2xl text-3xl leading-tight sm:text-4xl">The loop for every task type.</h2>
          </Reveal>
          <ol className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-5">
            {METHOD.map((m, i) => (
              <Reveal key={m.k} as="li">
                <span className="font-display text-2xl text-indigo-600 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <p className="mt-2 font-display text-[15px] font-semibold text-slate-900">{m.k}</p>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-500">{m.v}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* access */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <Reveal className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 px-8 py-14 text-center sm:px-16">
          <Grain />
          <div className="relative">
            <h2 className="h-display text-3xl text-white sm:text-4xl">Already have access?</h2>
            <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-slate-300">
              If your instructor or program has set you up, sign in and pick up exactly where you left off.
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <Link to="/login" className="cta" data-testid="landing-cta-signin">Sign in <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </Reveal>
      </section>

      <footer className="border-t border-slate-200">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-8">
          <Wordmark className="text-base" />
          <span className="max-w-xl text-xs leading-relaxed text-slate-400">
            CELPIP is a trademark of Paragon Testing Enterprises. PTE is a trademark of Pearson. This
            platform is independent and not affiliated with or endorsed by either organization.
          </span>
        </div>
      </footer>
    </div>
  );
}
